import { useEffect, useMemo, useState } from "react";
import { useUserContext } from "@/shared/features/user-context/presentation/hooks/useUserContext";
import {
  fetchAccountMergingCustomers,
  AccountMergingResponse,
  generateOtpForVerification,
  resendOtpForVerification,
  verifyContactDetail,
  processAccountMerging,
  AccountMergingPayload,
  OtpDeliveryMode
} from "../../application/services/accountMergingService";
import { formatPrice } from "@/shared/utils/priceFormatter";

export type AccountMergingFlowStep = "PRIMARY" | "SECONDARY";

interface OtpState {
  isOpen: boolean;
  isPreOpen: boolean;
  type: "self" | "re-assign" | null;
  phoneNumber: string;
  email?: string;
  otpId?: number;
}

export interface VerifiedTransferData {
  phoneNumber: string;
  otpId: number;
}

export function useAccountMerging() {
  const { userContext } = useUserContext();
  const [resendCountdown, setResendCountdown] = useState(0);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<AccountMergingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  
  const [flowStep, setFlowStep] = useState<AccountMergingFlowStep>("PRIMARY");
  const [primaryEmail, setPrimaryEmail] = useState<string | null>(null);
  const [secondaryActions, setSecondaryActions] = useState<Record<string, string>>({});
  
  const [transferInputs, setTransferInputs] = useState<Record<string, string>>({});
  const [transferErrors, setTransferErrors] = useState<Record<string, string>>({});

  const [verifiedTransfers, setVerifiedTransfers] = useState<Record<string, VerifiedTransferData>>({});
  
  const [otpState, setOtpState] = useState<OtpState>({
    isOpen: false,
    isPreOpen: false,
    type: null,
    phoneNumber: "",
  });
  
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isVerifyLoading, setIsVerifyLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState({ text: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const shouldOpen =
    userContext?.hasPhoneNumber &&
    userContext?.isLoggedIn &&
    userContext?.accountMergingPopupEnabled &&
    !userContext?.primaryAccountSet;

  const phoneNumberVerified = userContext?.phoneNumberVerified ?? false;

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const startCountdown = () => setResendCountdown(30);

  useEffect(() => {
    if (!shouldOpen) return;

    let isActive = true;

    const load = async () => {
      try {
        setOpen(true);
        setLoading(true);

        const res = await fetchAccountMergingCustomers();

        if (!isActive) return;

        if (!res.hasError) {
          setData(res);

          const emails = Object.keys(res.customerAccountsData);
          if (emails.length > 0) {
            setSecondaryActions({}); 
            
            const isSelfVerified = phoneNumberVerified;

            if (!isSelfVerified && res.customerPhone) {
              setOtpState({
                isOpen: false,
                isPreOpen: true,
                type: "self",
                phoneNumber: res.customerPhone,
              });
            }
          }
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    load();
    return () => { isActive = false; };
  }, [shouldOpen, phoneNumberVerified]);

  useEffect(() => {
    if (!open) {
      setFlowStep("PRIMARY");
      setPrimaryEmail(null);
      setSecondaryActions({});
      setTransferInputs({});
      setTransferErrors({});
      setVerifiedTransfers({});
      setOtpState({ isOpen: false, isPreOpen: false, type: null, phoneNumber: "" });
      setOtpMessage({ text: "", type: "" }); // clear messages
      setIsConfirmOpen(false);
    }
  }, [open]);

  const setSecondaryAction = (email: string, action: string) => {
    setSecondaryActions((prev) => ({ ...prev, [email]: action }));
  };

  const setTransferInput = (email: string, value: string) => {
    if (/^\d*$/.test(value) && value.length <= 10) {
      setTransferInputs((prev) => ({ ...prev, [email]: value }));
      if (transferErrors[email]) {
        setTransferErrors((prev) => ({ ...prev, [email]: "" }));
      }
    }
  };

  const clearSecondaryState = () => {
    setSecondaryActions({});
    setTransferInputs({});
    setTransferErrors({});
    setVerifiedTransfers({});
  };

  const sendInitialSelfOtp = async () => {
    if (!otpState.phoneNumber) return;

    setIsOtpLoading(true);

    try {
      const otpRes = await generateOtpForVerification(otpState.phoneNumber, true);

      if (!otpRes.hasError && otpRes.otpId) {
        setOtpState((prev) => ({
          ...prev,
          isPreOpen: false,
          isOpen: true,
          otpId: otpRes.otpId,
        }));
        startCountdown(); // Start timer
      } else {
        console.error("Failed to generate OTP:", otpRes.message);
      }
    } catch (error) {
      console.error("Network error generating OTP:", error);
    } finally {
      setIsOtpLoading(false);
    }
  };

  const requestOtp = async (phoneNumber: string, type: "re-assign", email: string) => {
    const regex = /^[6-9]\d{9}$/;
    if (!regex.test(phoneNumber)) {
      setTransferErrors((prev) => ({ ...prev, [email]: "Invalid Number" }));
      return;
    }

    setIsOtpLoading(true);
    setTransferErrors((prev) => ({ ...prev, [email]: "" }));

    try {
      const res = await generateOtpForVerification(phoneNumber, false);
      if (!res.hasError && res.otpId) {
        setOtpState({ isOpen: true, isPreOpen: false, type, phoneNumber, email, otpId: res.otpId });
        setOtpMessage({ text: "", type: "" }); // Reset messages on new request
        startCountdown();
      } else {
        setTransferErrors((prev) => ({ ...prev, [email]: res.message || "Failed to generate OTP." }));
      }
    } catch (error) {
      console.error(error);
      setTransferErrors((prev) => ({ ...prev, [email]: "Network error generating OTP." }));
    } finally {
      setIsOtpLoading(false);
    }
  };

  // submit otp logic
  const verifyOtp = async (otpString: string) => {
    if (!otpState.phoneNumber || !otpState.otpId || !otpState.type) return;

    setIsVerifyLoading(true);
    setOtpMessage({ text: "", type: "" });

    try {
      const isVerificationBeforeMerging = otpState.type === "self";
      
      const res = await verifyContactDetail(
        otpState.phoneNumber,
        otpState.otpId,
        otpString,
        isVerificationBeforeMerging
      );

      if (!res.hasError) {
        if (otpState.type === "re-assign" && otpState.email && otpState.otpId) {
          setVerifiedTransfers((prev) => ({
            ...prev,
            [otpState.email!]: {
              phoneNumber: otpState.phoneNumber,
              otpId: otpState.otpId!
            },
          }));
        }
        // close otp
        setOtpState({ isOpen: false, isPreOpen: false, type: null, phoneNumber: "" });
      } else {
        // API error
        setOtpMessage({ text: res.message || "Invalid OTP entered.", type: "error" });
      }
    } catch (error) {
      console.error(error);
      setOtpMessage({ text: "Network error verifying OTP.", type: "error" });
    } finally {
      setIsVerifyLoading(false);
    }
  };

  const proceedToSecondary = () => {
    if (primaryEmail) {
      setFlowStep("SECONDARY");
    }
  };

  const goBackToPrimary = () => {
    setSecondaryActions({});
    setTransferInputs({});
    setTransferErrors({});
    setVerifiedTransfers({});
    setFlowStep("PRIMARY");
  };

  const closeOtp = () => {
    setOtpState({ isOpen: false, isPreOpen: false, type: null, phoneNumber: "" });
    setOtpMessage({ text: "", type: "" });
    setResendCountdown(0);
  };

  const resendOtp = async (mode?: OtpDeliveryMode) => {
    if (!otpState.phoneNumber || !otpState.otpId || !otpState.type) return;

    setIsResendLoading(true);
    setOtpMessage({ text: "", type: "" });

    try {
      const isVerificationBeforeMerging = otpState.type === "self";
      const res = await resendOtpForVerification(
        otpState.phoneNumber,
        otpState.otpId,
        isVerificationBeforeMerging,
        mode
      );

      if (!res.hasError && res.otpId) {
        setOtpState((prev) => ({ ...prev, otpId: res.otpId }));
        setOtpMessage({ text: "OTP Resent successfully", type: "success" });
        startCountdown(); // restart timer
      } else {
        setOtpMessage({ text: res.message || "Failed to resend OTP.", type: "error" });
      }
    } catch (error) {
      console.error(error);
      setOtpMessage({ text: "Network error resending OTP.", type: "error" });
    } finally {
      setIsResendLoading(false);
    }
  };

  const submitMerging = async () => {
    if (!primaryEmail || !data) return;

    setIsSubmitting(true);

    const secondaryAccounts = Object.keys(data.customerAccountsData).filter((email) => email !== primaryEmail);

    const payload: AccountMergingPayload = {
      primaryAccountEmail: primaryEmail,
      accountsToBeDiscarded: [],
      accountsToBeMerged: [],
      accountsToBeTransferred: [],
    };

    secondaryAccounts.forEach((email) => {
      const action = secondaryActions[email];

      if (action === "merge") {
        payload.accountsToBeMerged.push(email);
      } else if (action === "discard") {
        payload.accountsToBeDiscarded.push(email);
      } else if (action === "re-assign") {
        const transferData = verifiedTransfers[email];
        if (transferData) {
          payload.accountsToBeTransferred.push({
            email: email,
            newPhoneNumber: transferData.phoneNumber,
            otpId: transferData.otpId,
          });
        }
      }
    });

    try {
      const res = await processAccountMerging(payload);
      if (!res.hasError) {
        if (res.isLogoutRequired) {
          window.location.href = '/signout'; 
          return; 
        } // logout or refresh

        window.location.reload();
      } else {
        console.error("Merging failed:", res.message);
      }
    } catch (error) {
      console.error("Network error during submission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (!primaryEmail || !data) return true;

    const secondaryAccounts = Object.keys(data.customerAccountsData)
      .filter((email) => email !== primaryEmail);

    return secondaryAccounts.some((email) => {
      const action = secondaryActions[email];

      if (!action) return true;

      if (action === "re-assign") {
        const inputNum = transferInputs[email];
        const verifiedData = verifiedTransfers[email];

        if (!verifiedData) return true;
        if (inputNum !== verifiedData.phoneNumber) return true;
      }

      return false;
    });
  }, [primaryEmail, data, secondaryActions, transferInputs, verifiedTransfers]);

  const handleLogout = () => {
    window.location.href = `${window.location.origin}/signout`;
  };

  return {
    open,
    setOpen,
    data,
    loading,
    flowStep,
    setFlowStep,
    goBackToPrimary,
    proceedToSecondary,
    primaryEmail,
    setPrimaryEmail,
    secondaryActions,
    setSecondaryAction,
    transferInputs,
    setTransferInput,
    transferErrors,
    verifiedTransfers,
    clearSecondaryState,
    otpState,
    isOtpLoading,
    isVerifyLoading,
    isResendLoading,
    isSubmitting,
    otpMessage,
    sendInitialSelfOtp,
    requestOtp,
    verifyOtp,
    resendOtp,
    closeOtp,
    submitMerging,
    isSubmitDisabled,
    isConfirmOpen,
    setIsConfirmOpen,
    handleLogout,
    resendCountdown,
  };
}

export function formatAccountStats(details: AccountMergingResponse["customerAccountsData"][string]) {
  return [
    { label: "Orders", value: details.orderCount },
    { label: "Blue Cash", value: formatPrice(details.blueCash) }, 
    { label: "Blue Credits", value: formatPrice(details.blueCredit) }, 
    { label: "Active Gold Mine Plans", value: details.activeGMSPlans },
    { label: "Active Gold Reserve Plans", value: details.activeGRPPlans },
    { label: "Vouchers", value: details.voucherCount },
  ];
}